/**
 * Router SPA simple para el ERP Taller Vargas
 * Maneja navegación entre módulos sin recarga de página
 */

import { escapeHtml } from './utils.js';

const routes = {
  '/':             () => import('./pages/ordenes.js'),
  '/login':        () => import('./pages/login.js'),
  '/taller':       () => import('./pages/taller.js'),
  '/ordenes':      () => import('./pages/ordenes.js'),
  '/clientes':     () => import('./pages/clientes.js'),
  '/clientes/editar': () => import('./pages/gestion-clientes.js'),
  '/repuestos':     () => import('./pages/repuestos.js'),
  '/proformas':    () => import('./pages/proformas.js'),
  '/operaciones':  () => import('./pages/operaciones.js'),
  '/facturacion':  () => import('./pages/facturacion.js'),
  '/usuarios':     () => import('./pages/usuarios.js'),
  '/confirmar':    () => import('./pages/confirmar.js'),
};

/** Verifica si el usuario tiene sesión activa */
function isAuthenticated() {
  return !!localStorage.getItem('vargas_token');
}

let currentModule = null;

export async function navigate(path = '/') {
  if (window.updateUserSidebar) {
    window.updateUserSidebar();
  }
  const pathname = path.split('?')[0];
  const matchedPath = pathname.startsWith('/confirmar') ? '/confirmar' : pathname;

  // Guard de autenticación: rutas públicas
  const publicRoutes = ['/login', '/confirmar'];
  if (!publicRoutes.includes(matchedPath) && !isAuthenticated()) {
    return navigate('/login');
  }

  // Si ya está logueado y va al login, redirigir según su rol
  if (matchedPath === '/login' && isAuthenticated()) {
    const userStr = localStorage.getItem('vargas_user');
    const user = userStr ? JSON.parse(userStr) : null;
    return navigate(user?.rol === 'operario' ? '/taller' : '/ordenes');
  }

  // Redirección de inicio: operario va a /taller, admin va directo a /ordenes
  if (matchedPath === '/' && isAuthenticated()) {
    const userStr = localStorage.getItem('vargas_user');
    const user = userStr ? JSON.parse(userStr) : null;
    return navigate(user?.rol === 'operario' ? '/taller' : '/ordenes');
  }

  // Protección de módulos administrativos
  if (matchedPath === '/facturacion' || matchedPath === '/usuarios' || matchedPath === '/clientes/editar' || matchedPath === '/repuestos') {
    const userStr = localStorage.getItem('vargas_user');
    const user = userStr ? JSON.parse(userStr) : null;
    if (!user || user.rol !== 'administrador') {
      alert('Acceso restringido. Solo administradores pueden acceder a esta sección.');
      return navigate(user?.rol === 'operario' ? '/taller' : '/');
    }
  }

  const loader = routes[matchedPath] || routes['/'];
  
  const sidebar = document.getElementById('sidebar-menu');
  const menuBtn = document.getElementById('btn-menu-toggle-header');
  const globalHeader = document.querySelector('header');
  const mainParent = document.querySelector('.flex-1.flex.flex-col.overflow-hidden.relative');

  if (matchedPath === '/confirmar' || matchedPath === '/login') {
    if (sidebar) sidebar.style.display = 'none';
    if (menuBtn) menuBtn.style.display = 'none';
    if (globalHeader) globalHeader.style.display = 'none';
    if (mainParent) {
      mainParent.style.padding = '0';
      mainParent.style.margin = '0';
    }
  } else {
    if (sidebar) sidebar.style.display = '';
    if (menuBtn) menuBtn.style.display = '';
    if (globalHeader) globalHeader.style.display = '';
    if (mainParent) {
      mainParent.style.padding = '';
      mainParent.style.margin = '';
    }
  }

  // Actualizar sidebar activo
  document.querySelectorAll('.sidebar-item').forEach(el => {
    const route = el.dataset.route;
    const isActive = (route === matchedPath);
    el.classList.toggle('sidebar-active', isActive);
  });



  // Cerrar sidebar si está abierto en móvil
  const backdrop = document.getElementById('sidebar-backdrop');
  if (sidebar && sidebar.classList.contains('sidebar-open')) {
    sidebar.classList.remove('sidebar-open');
    if (backdrop) backdrop.classList.add('hidden');
  }

  // Limpiar módulo anterior
  if (currentModule?.destroy) currentModule.destroy();

  // Mostrar spinner de carga
  const main = document.getElementById('main-content');
  main.innerHTML = `
    <div class="flex items-center justify-center h-full">
      <div class="flex flex-col items-center gap-3">
        <div class="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        <p class="text-slate-400 text-sm font-medium">Cargando módulo...</p>
      </div>
    </div>`;

  try {
    // Actualizar URL y breadcrumb antes de inicializar para que los módulos lean query params (?abrir=ID, ?q=...)
    history.pushState({ path }, '', path);
    updateBreadcrumb(path);

    const module = await loader();
    currentModule = module;
    await module.init(main);
  } catch (err) {
    console.error('[Router] Error cargando módulo:', err);
    main.innerHTML = `
      <div class="flex items-center justify-center h-full">
        <div class="text-center">
          <p class="text-red-500 font-bold text-lg">Error cargando el módulo</p>
          <p class="text-slate-400 text-sm mt-1">${escapeHtml(err.message || 'Error inesperado')}</p>
          <button onclick="navigate('/')" class="mt-4 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-bold">
            Volver al inicio
          </button>
        </div>
      </div>`;
  }
}

const breadcrumbs = {
  '/':             ['Taller · Autos de hoy', 'Gestión de órdenes y atención'],
  '/taller':       ['Taller Mecánico', 'Portal de Trabajo y Diagnóstico'],
  '/ordenes':      ['Órdenes de Servicio', 'Gestión Operativa'],
  '/clientes':     ['CRM Clientes', 'Seguimiento 360° y Fidelización'],
  '/clientes/editar': ['Administrar clientes', 'Edición de datos y contacto'],
  '/repuestos':    ['Repuestos', 'Catálogo, existencias e historial de movimientos'],
  '/proformas':    ['Cotizaciones', 'Presupuestos y Proformas'],
  '/operaciones':  ['Taller y Operaciones', 'Control en Vivo, Equipo Técnico y Tablero'],
  '/facturacion':  ['Finanzas', 'Facturación y Cobros'],
  '/usuarios':     ['Personal', 'Administración de usuarios'],
};

function updateBreadcrumb(path) {
  const [mod, sub] = breadcrumbs[path] || ['', ''];
  const modEl = document.getElementById('breadcrumb-modulo');
  const subEl = document.getElementById('breadcrumb-sub');
  if (modEl) modEl.textContent = mod;
  if (subEl) subEl.textContent = sub;
}

// Manejar botón atrás del navegador
window.addEventListener('popstate', (e) => {
  navigate(e.state?.path || '/');
});

// Exponer para HTML
window.navigate = navigate;
